import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { isAdmin, getRole } from '../utils/role';

const emptyForm = {
  subject: '', description: '', priorityId: '', categoryId: '', subcategoryId: '',
  companyId: '', projectId: '', assignedTo: '', requesterName: '', requesterEmail: '',
};

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [scope, setScope] = useState('');
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  // masters
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [selected, setSelected] = useState(null); // ticket detail

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (scope) params.scope = scope;
      const { data } = await api.get('/tickets', { params });
      setTickets(data);
    } catch (e) {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [statusFilter, scope]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [c, p, a, pr, st, cat, sub] = await Promise.all([
          api.get('/companies'), api.get('/projects'), api.get('/users/agents'),
          api.get('/priorities'), api.get('/statuses'),
          api.get('/categories'), api.get('/categories/subcategories/all'),
        ]);
        setCompanies(c.data); setProjects(p.data); setAgents(a.data);
        setPriorities(pr.data); setStatuses(st.data); setCategories(cat.data); setSubcategories(sub.data);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const companyName = (id) => companies.find(c => c.id === id)?.name || '—';
  const projectName = (id) => projects.find(p => p.id === id)?.projectName || '—';
  const projectsFor = (companyId) =>
    companyId ? projects.filter(p => String(p.companyId) === String(companyId)) : [];
  const subsFor = (categoryId) =>
    categoryId ? subcategories.filter(s => String(s.categoryId) === String(categoryId)) : [];
  const priColor = (name) => priorities.find(p => p.name === name)?.color || '#94a3b8';
  const statColor = (name) => statuses.find(s => s.name === name)?.color || '#64748b';

  // Role-aware scope tabs (server already restricts engineers / customer managers).
  const role = getRole();
  const showMy = ['ADMIN', 'AGENT', 'SUPPORT_MANAGER', 'TEAM_LEAD'].includes(role);
  const showTeam = ['SUPPORT_MANAGER', 'TEAM_LEAD'].includes(role);
  const scopeTabs = showMy
    ? [{ k: '', label: 'All' }, { k: 'MY', label: 'My Tickets' }, ...(showTeam ? [{ k: 'TEAM', label: 'My Team' }] : [])]
    : [];

  const openCreate = () => {
    const medium = priorities.find(p => p.level === 2) || priorities[0];
    setForm({ ...emptyForm, priorityId: medium ? String(medium.id) : '' });
    setFormError(''); setShowCreate(true);
  };

  const submitCreate = async () => {
    setFormError('');
    if (!form.subject.trim()) { setFormError('Subject is required'); return; }
    setSaving(true);
    try {
      await api.post('/tickets', {
        subject: form.subject,
        description: form.description,
        priorityId: form.priorityId ? Number(form.priorityId) : null,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        subcategoryId: form.subcategoryId ? Number(form.subcategoryId) : null,
        companyId: form.companyId ? Number(form.companyId) : null,
        projectId: form.projectId ? Number(form.projectId) : null,
        assignedTo: form.assignedTo || null,
        requesterName: form.requesterName, requesterEmail: form.requesterEmail,
      });
      setShowCreate(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      setFormError(e.response?.data?.error || 'Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };

  const Badge = ({ text, color }) => (
    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, color: '#fff', background: color, borderRadius: 999, padding: '2px 10px' }}>{text}</span>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Tickets</h1>
          <p style={{ color: '#64748b' }}>Manage and track support requests</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>+ New Ticket</button>
      </div>

      {scopeTabs.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {scopeTabs.map(s => (
            <button key={s.k} className={scope === s.k ? 'btn-primary' : 'btn-ghost'} onClick={() => setScope(s.k)}>{s.label}</button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className={statusFilter === '' ? 'btn-primary' : 'btn-ghost'} onClick={() => setStatusFilter('')}>All</button>
        {statuses.map(s => (
          <button key={s.id} className={statusFilter === s.name ? 'btn-primary' : 'btn-ghost'} onClick={() => setStatusFilter(s.name)}>{s.name}</button>
        ))}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
        ) : tickets.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No tickets found.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Ticket</th><th>Subject</th><th>Company</th><th>Project</th><th>Priority</th><th>Status</th><th>Due</th><th>Assigned</th></tr>
            </thead>
            <tbody>
              {tickets.map(t => {
                const overdue = t.dueAt && new Date(t.dueAt) < new Date() && !statuses.find(s => s.name === t.status)?.closed;
                return (
                  <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(t)}>
                    <td style={{ fontWeight: 600 }}>{t.ticketNumber || `#${t.id}`}</td>
                    <td>{t.subject}</td>
                    <td>{companyName(t.companyId)}</td>
                    <td>{t.projectId ? projectName(t.projectId) : '—'}</td>
                    <td><Badge text={t.priority} color={priColor(t.priority)} /></td>
                    <td><Badge text={t.status} color={statColor(t.status)} /></td>
                    <td style={{ fontSize: 12, color: overdue ? '#dc2626' : '#64748b', fontWeight: overdue ? 600 : 400 }}>
                      {t.dueAt ? new Date(t.dueAt).toLocaleDateString() : '—'}{overdue ? ' ⚠' : ''}
                    </td>
                    <td>{t.assignedTo || <span style={{ color: '#94a3b8' }}>Unassigned</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="New Ticket">
          {formError && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{formError}</div>}
          <Field label="Subject *">
            <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Short summary" />
          </Field>
          <Field label="Description">
            <textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Describe the issue" />
          </Field>
          <Row>
            <Field label="Priority">
              <select value={form.priorityId} onChange={e => setForm({ ...form, priorityId: e.target.value })}>
                <option value="">— None —</option>
                {priorities.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <select value={form.categoryId} onChange={e => setForm({ ...form, categoryId: e.target.value, subcategoryId: '' })}>
                <option value="">— None —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Subcategory">
              <select value={form.subcategoryId} onChange={e => setForm({ ...form, subcategoryId: e.target.value })} disabled={!form.categoryId}>
                <option value="">{form.categoryId ? '— None —' : 'Select a category first'}</option>
                {subsFor(form.categoryId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Assign to">
              <select value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}>
                <option value="">— Unassigned —</option>
                {agents.map(a => <option key={a.username} value={a.username}>{a.fullName} ({a.username})</option>)}
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Company">
              <select value={form.companyId} onChange={e => setForm({ ...form, companyId: e.target.value, projectId: '' })}>
                <option value="">— None —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Project">
              <select value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })} disabled={!form.companyId}>
                <option value="">{form.companyId ? '— None —' : 'Select a company first'}</option>
                {projectsFor(form.companyId).map(p => <option key={p.id} value={p.id}>{p.projectCode ? p.projectCode + ' — ' : ''}{p.projectName}</option>)}
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Requester name">
              <input value={form.requesterName} onChange={e => setForm({ ...form, requesterName: e.target.value })} placeholder="Optional" />
            </Field>
            <Field label="Requester email">
              <input value={form.requesterEmail} onChange={e => setForm({ ...form, requesterEmail: e.target.value })} placeholder="Optional" />
            </Field>
          </Row>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn-primary" onClick={submitCreate} disabled={saving} style={{ flex: 1 }}>{saving ? 'Creating...' : 'Create Ticket'}</button>
            <button className="btn-ghost" onClick={() => setShowCreate(false)} style={{ flex: 1 }}>Cancel</button>
          </div>
        </Modal>
      )}

      {selected && (
        <TicketDetail
          ticket={selected}
          companies={companies} projects={projects} agents={agents}
          priorities={priorities} statuses={statuses} categories={categories} subcategories={subcategories}
          onClose={() => setSelected(null)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}

function TicketDetail({ ticket, companies, projects, agents, priorities, statuses, categories, subcategories, onClose, onChanged }) {
  const projectsFor = (companyId) =>
    companyId ? projects.filter(p => String(p.companyId) === String(companyId)) : [];
  const subsFor = (categoryId) =>
    categoryId ? subcategories.filter(s => String(s.categoryId) === String(categoryId)) : [];
  const [t, setT] = useState(ticket);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [internal, setInternal] = useState(false);
  const [posting, setPosting] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${ticket.id}/comments`);
      setComments(data);
    } catch (e) { /* ignore */ }
  }, [ticket.id]);

  useEffect(() => { setT(ticket); loadComments(); }, [ticket, loadComments]);

  const saveMeta = async (patch) => {
    const updated = { ...t, ...patch };
    setT(updated);
    setSavingMeta(true);
    try {
      const { data } = await api.put(`/tickets/${t.id}`, updated);
      setT(data);
      onChanged();
    } catch (e) { /* ignore */ } finally { setSavingMeta(false); }
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/tickets/${t.id}/comments`, { body: newComment, internal });
      setNewComment('');
      setInternal(false);
      loadComments();
    } catch (e) { /* ignore */ } finally { setPosting(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this ticket permanently?')) return;
    try {
      await api.delete(`/tickets/${t.id}`);
      onChanged();
      onClose();
    } catch (e) { /* ignore */ }
  };

  const overdue = t.dueAt && new Date(t.dueAt) < new Date() && !statuses.find(s => s.name === t.status)?.closed;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'flex-end', zIndex: 900 }} onClick={onClose}>
      <div style={{ width: 520, maxWidth: '95vw', height: '100vh', background: '#fff', overflowY: 'auto', boxShadow: '-8px 0 30px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: 24, borderBottom: '1px solid var(--gray-200)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <div style={{ fontSize: 13, color: '#7c3aed', fontWeight: 600 }}>{t.ticketNumber || `#${t.id}`}</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{t.subject}</h2>
            </div>
            <button className="btn-ghost" onClick={onClose}>✕</button>
          </div>
          {t.description && <p style={{ marginTop: 12, color: '#475569', whiteSpace: 'pre-wrap' }}>{t.description}</p>}
          <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: '#64748b' }}>
            <span>Support Manager: <strong>{t.supportManager || '—'}</strong></span>
            <span style={{ color: overdue ? '#dc2626' : '#64748b' }}>Due: <strong>{t.dueAt ? new Date(t.dueAt).toLocaleString() : '—'}</strong>{overdue ? ' (overdue)' : ''}</span>
          </div>
        </div>

        <div style={{ padding: 24, borderBottom: '1px solid var(--gray-200)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Status">
            <select value={t.statusId || ''} onChange={e => saveMeta({ statusId: e.target.value ? Number(e.target.value) : null })} disabled={savingMeta}>
              <option value="">— None —</option>
              {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Priority">
            <select value={t.priorityId || ''} onChange={e => saveMeta({ priorityId: e.target.value ? Number(e.target.value) : null })} disabled={savingMeta}>
              <option value="">— None —</option>
              {priorities.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Category">
            <select value={t.categoryId || ''} onChange={e => saveMeta({ categoryId: e.target.value ? Number(e.target.value) : null, subcategoryId: null })} disabled={savingMeta}>
              <option value="">— None —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Subcategory">
            <select value={t.subcategoryId || ''} onChange={e => saveMeta({ subcategoryId: e.target.value ? Number(e.target.value) : null })} disabled={savingMeta || !t.categoryId}>
              <option value="">{t.categoryId ? '— None —' : '—'}</option>
              {subsFor(t.categoryId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Assigned to">
            <select value={t.assignedTo || ''} onChange={e => saveMeta({ assignedTo: e.target.value || null })} disabled={savingMeta}>
              <option value="">— Unassigned —</option>
              {agents.map(a => <option key={a.username} value={a.username}>{a.fullName} ({a.username})</option>)}
            </select>
          </Field>
          <Field label="Company">
            <select value={t.companyId || ''} onChange={e => saveMeta({ companyId: e.target.value ? Number(e.target.value) : null, projectId: null })} disabled={savingMeta}>
              <option value="">— None —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Project">
            <select value={t.projectId || ''} onChange={e => saveMeta({ projectId: e.target.value ? Number(e.target.value) : null })} disabled={savingMeta || !t.companyId}>
              <option value="">{t.companyId ? '— None —' : 'Select a company first'}</option>
              {projectsFor(t.companyId).map(p => <option key={p.id} value={p.id}>{p.projectCode ? p.projectCode + ' — ' : ''}{p.projectName}</option>)}
            </select>
          </Field>
        </div>

        <div style={{ padding: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Comments</h3>
          {comments.length === 0 && <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 12 }}>No comments yet.</div>}
          {comments.map(c => (
            <div key={c.id} style={{
              padding: 12, borderRadius: 10, marginBottom: 10,
              background: c.internal ? '#fef9c3' : 'var(--gray-50)',
              border: '1px solid var(--gray-200)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>{c.author}{c.internal ? ' · internal' : ''}</span>
                <span>{new Date(c.createdAt).toLocaleString()}</span>
              </div>
              <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{c.body}</div>
            </div>
          ))}

          <textarea rows={3} value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Add a comment..." style={{ marginTop: 8 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
            {getRole() === 'CUSTOMER_MANAGER' ? <span /> : (
              <label style={{ fontSize: 13, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} style={{ width: 'auto' }} />
                Internal note
              </label>
            )}
            <button className="btn-primary" onClick={postComment} disabled={posting}>{posting ? 'Posting...' : 'Add Comment'}</button>
          </div>
        </div>

        {isAdmin() && (
          <div style={{ padding: 24, borderTop: '1px solid var(--gray-200)' }}>
            <button className="btn-danger" onClick={remove}>Delete Ticket</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontWeight: 700, marginBottom: 20, color: '#0f172a', fontSize: 18 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4, color: '#374151' }}>{label}</label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>;
}
